// File: components/DutyList/DutyList.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Filters from "./Filters";
import RoleSelector from "./RoleSelector";
import ExportButtons from "./ExportButtons";
import UrduPrintable from "./UrduPrintable";
import DutyTable from "./DutyTable";
import { translateText } from "./utils/translation";

const DutyLists = () => {
  const [duties, setDuties] = useState([]);
  const [filter, setFilter] = useState({ date: "", section: "", name: "" });
  const [doctorList, setDoctorList] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editData, setEditData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRoles, setSelectedRoles] = useState({
    Doctor: true,
    Nurse: false,
    FireFighting: false,
    RapidResponseGround: false,
    RapidResponseFirst: false,
  });
  const [fireTeam, setFireTeam] = useState([]);
  const [urduData, setUrduData] = useState([]);
  const [loading, setLoading] = useState(false);

  const itemsPerPage = 8;
  const printRef = useRef();
  const tableRef = useRef();
  const fileDate = filter.date || new Date().toISOString().split("T")[0];
  const readableDate = filter.date
    ? new Date(filter.date).toLocaleDateString("en-GB")
    : new Date().toLocaleDateString("en-GB");

  useEffect(() => {
    axios.get("https://ricgop.online/backend/getDuties.php").then((res) => {
      const cleaned = res.data.map((d) => ({
        ...d,
        name: d.name.trim(),
        contact: d.contact.trim(),
        role: d.role.trim(),
        section: d.section.trim(),
        on_call: d.on_call.trim(),
        duty_date: d.duty_date.trim(),
      }));
      setDuties(cleaned);
      const names = [...new Set(cleaned.map((d) => d.name))];
      setDoctorList(names);
    });

    axios.get("https://ricgop.online/backend/getFireTeam.php").then((res) => {
      const fireCleaned = res.data.map((f) => ({
        delegation: (f.delegation_field || "").trim(),
        responsible: (f.responsible_person || "").trim(),
        created_at: (f.created_at || "").trim(),
      }));
      setFireTeam(fireCleaned);
    });
  }, []);

  const filtered = duties.filter(
    (d) =>
      (!filter.date || d.duty_date === filter.date) &&
      (!filter.section ||
        d.section.toLowerCase().includes(filter.section.toLowerCase())) &&
      (!filter.name || d.name === filter.name)
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  useEffect(() => {
    const translateCurrentItems = async () => {
      setLoading(true);
      try {
        const translated = await Promise.all(
          currentItems.map(async (row, i) => ({
            no: await translateText((indexOfFirstItem + i + 1).toString()),
            on_call: await translateText(row.on_call || ""),
            name: await translateText(row.name || ""),
            contact: await translateText(row.contact || ""),
          }))
        );
        setUrduData(translated);
      } catch (err) {
        console.error("Translation failed:", err);
        setUrduData([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentItems.length > 0) {
      translateCurrentItems();
    } else {
      setUrduData([]);
      setLoading(false);
    }
  }, [currentPage, indexOfFirstItem]);

  const handleEdit = (id) => {
    const duty = duties.find((d) => d.id === id);
    if (duty) {
      setEditingIndex(id);
      setEditData({ ...duty });
    }
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditData({});
  };

  const handleSaveEdit = () => {
    const updated = duties.map((d) =>
      d.id === editingIndex ? { ...d, ...editData } : d
    );
    setDuties(updated);
    setEditingIndex(null);
    setEditData({});
  };

  const isFilterActive = filter.date || filter.name || filter.section;

  return (
    <div>
      <Filters
        filter={filter}
        setFilter={setFilter}
        setCurrentPage={setCurrentPage}
        doctorList={doctorList}
      />

      <RoleSelector selectedRoles={selectedRoles} setSelectedRoles={setSelectedRoles} />

      {isFilterActive && (
        <>
          <ExportButtons
            selectedRoles={selectedRoles}
            filtered={filtered}
            fireTeam={fireTeam}
            fileDate={fileDate}
            readableDate={readableDate}
            urduData={urduData}
            loading={loading}
            printRef={printRef}
            tableRef={tableRef}
          />

          <UrduPrintable
            loading={loading}
            urduData={urduData}
            readableDate={readableDate}
            printRef={printRef}
          />

          <DutyTable
            currentItems={currentItems}
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            editingIndex={editingIndex}
            editData={editData}
            handleEdit={handleEdit}
            handleEditChange={handleEditChange}
            handleCancelEdit={handleCancelEdit}
            handleSaveEdit={handleSaveEdit}
            indexOfFirstItem={indexOfFirstItem}
            tableRef={tableRef}
          />
        </>
      )}
    </div>
  );
};

export default DutyLists;
